/**
 * 全景项目：列表 / 详情 / 上传登记（A + OSS，无 B）
 */
import { useCallback, useState } from 'react';
import apiFetch from '../utils/apiFetch';
import { message } from '../utils/antdMessage';

async function readJson(res) {
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    throw new Error(data.error || data.message || `请求失败 ${res.status}`);
  }
  return data;
}

export default function usePanoramaProjects({
  canManage = false,
  canView = false,
  holdPanelOpenRef,
  setExpanded,
  setPinned,
  /** 与路网/航拍一致：开二级菜单时自动固定，关菜单后可自动解锁 */
  pinForSecondaryMenu
} = {}) {
  const [projects, setProjects] = useState([]);
  const [busy, setBusy] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [kindChecked, setKindChecked] = useState(false);
  const [viewer, setViewer] = useState(null); // { projectId, sceneId? }
  /** 后台「地图设置」：是否在地图标注有坐标的全景 */
  const [showMapMarkers, setShowMapMarkers] = useState(true);
  const [markerSize, setMarkerSize] = useState(22);
  const [markerIconUrl, setMarkerIconUrl] = useState(null);
  const [mapPins, setMapPins] = useState([]);

  const loadMapSettings = useCallback(async () => {
    try {
      const res = await apiFetch('/api/admin/map-settings');
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) return;
      setShowMapMarkers(data?.settings?.panorama_show_map_markers !== false);
      const sz = Number(data?.settings?.panorama_marker_size);
      setMarkerSize(Number.isFinite(sz) ? sz : 22);
      setMarkerIconUrl(data?.settings?.panorama_marker_icon_url || null);
    } catch (_) { /* 保持默认 */ }
  }, []);

  const loadMapPins = useCallback(async () => {
    if (!canView && !canManage) {
      setMapPins([]);
      return [];
    }
    try {
      const data = await readJson(await apiFetch('/api/panorama/map-pins'));
      const pins = data.pins || [];
      setMapPins(pins);
      return pins;
    } catch (e) {
      console.warn('[panorama] load map-pins', e);
      setMapPins([]);
      return [];
    }
  }, [canManage, canView]);

  const loadProjects = useCallback(async ({ publishedOnly = true, withScenes = false } = {}) => {
    if (!canView && !canManage) return;
    try {
      const qs = new URLSearchParams();
      if (publishedOnly && !canManage) qs.set('status', 'published');
      if (withScenes) qs.set('withScenes', '1');
      const q = qs.toString() ? `?${qs.toString()}` : '';
      const data = await readJson(await apiFetch(`/api/panorama/projects${q}`));
      setProjects(data.projects || []);
    } catch (e) {
      console.warn('[panorama] load projects', e);
    }
  }, [canManage, canView]);

  const openMenu = () => {
    if (typeof pinForSecondaryMenu === 'function') {
      pinForSecondaryMenu();
    } else {
      setPinned?.(true);
      setExpanded?.(true);
    }
    setMenuOpen(true);
    setKindChecked(true);
    loadProjects({ publishedOnly: !canManage, withScenes: true });
  };

  const closeMenu = () => setMenuOpen(false);

  const onMenuOpenChange = (open) => {
    if (open) openMenu();
    else closeMenu();
  };

  const onKindMasterCheck = (checked) => {
    if (checked) {
      openMenu();
      return;
    }
    setKindChecked(false);
    setMenuOpen(false);
    setViewer(null);
  };

  const openManage = () => {
    holdPanelOpenRef && (holdPanelOpenRef.current = true);
    setPinned?.(true);
    setExpanded?.(true);
    setManageOpen(true);
    loadProjects({ publishedOnly: false, withScenes: true });
  };

  const closeManage = () => {
    setManageOpen(false);
    if (holdPanelOpenRef) holdPanelOpenRef.current = false;
  };

  const createProject = async (title, description = '') => {
    setBusy(true);
    try {
      const data = await readJson(await apiFetch('/api/panorama/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, description })
      }));
      message.success('已创建全景项目');
      await loadProjects({ publishedOnly: false, withScenes: true });
      return data.project;
    } finally {
      setBusy(false);
    }
  };

  const patchProject = async (id, body) => {
    setBusy(true);
    try {
      const data = await readJson(await apiFetch(`/api/panorama/projects/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      }));
      await loadProjects({ publishedOnly: false, withScenes: true });
      try { await loadMapPins(); } catch (_) { /* ignore */ }
      return data.project;
    } finally {
      setBusy(false);
    }
  };

  const loadBundle = useCallback(async (projectId) => {
    const data = await readJson(await apiFetch(`/api/panorama/projects/${projectId}`));
    return data;
  }, []);

  const createScene = async (projectId, name) => {
    const data = await readJson(await apiFetch(`/api/panorama/projects/${projectId}/scenes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name })
    }));
    return data.scene;
  };

  const uploadEquirect = async (projectId, sceneId, file, onProgress, options = {}) => {
    // 经 A 写入 OSS：output 桶常未配 CORS，浏览器直传会报「OSS 网络错误」
    const form = new FormData();
    form.append('file', file);
    form.append('kind', 'equirect');
    if (options.sceneName) form.append('sceneName', options.sceneName);
    if (typeof onProgress === 'function') onProgress({ percent: 10 });
    const data = await readJson(await apiFetch(`/api/panorama/scenes/${sceneId}/upload`, {
      method: 'POST',
      body: form
    }));
    if (typeof onProgress === 'function') onProgress({ percent: 100 });
    if (!data.scene?.ossKey && data.objectKey) {
      await readJson(await apiFetch(`/api/panorama/scenes/${sceneId}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ossKey: data.objectKey,
          name: options.sceneName || file.name.replace(/\.[^.]+$/, '')
        })
      }));
    }
    message.success('全景图已上传');
  };

  const patchScene = async (sceneId, body) => {
    const data = await readJson(await apiFetch(`/api/panorama/scenes/${sceneId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }));
    try { await loadMapPins(); } catch (_) { /* ignore */ }
    return data.scene;
  };

  const deleteScene = async (sceneId) => {
    await readJson(await apiFetch(`/api/panorama/scenes/${sceneId}`, { method: 'DELETE' }));
  };

  const createHotspot = async (projectId, body) => {
    const data = await readJson(await apiFetch(`/api/panorama/projects/${projectId}/hotspots`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }));
    return data.hotspot;
  };

  const deleteHotspot = async (id) => {
    await readJson(await apiFetch(`/api/panorama/hotspots/${id}`, { method: 'DELETE' }));
  };

  const openViewer = (projectId, sceneId = null) => {
    setPinned?.(true);
    setExpanded?.(true);
    setViewer({ projectId, sceneId });
  };

  const closeViewer = () => setViewer(null);

  return {
    projects,
    busy,
    manageOpen,
    menuOpen,
    kindChecked,
    viewer,
    showMapMarkers,
    markerSize,
    markerIconUrl,
    mapPins,
    loadMapSettings,
    loadMapPins,
    loadProjects,
    openMenu,
    closeMenu,
    onMenuOpenChange,
    onKindMasterCheck,
    openManage,
    closeManage,
    createProject,
    patchProject,
    loadBundle,
    createScene,
    uploadEquirect,
    patchScene,
    deleteScene,
    createHotspot,
    deleteHotspot,
    openViewer,
    closeViewer,
    setManageOpen,
    setMenuOpen,
    setKindChecked
  };
}
